"use client"

/**
 * Clinical Education dashboard: three work-area cards, one door per hub.
 */

import { Link } from "react-router"

import { Badge, BadgeCount } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/page-header"
import { StatusBadge } from "@/components/ui/status-badge"
import { Tip } from "@/components/ui/tip"
import {
  CLINICAL_DASHBOARD_COLUMNS,
  clinicalDashboardHref,
  type ClinicalDashboardBadge,
  type ClinicalDashboardItem,
} from "@/lib/prism-clinical-dashboard"
import { cn } from "@/lib/utils"

const STRETCHED =
  "after:absolute after:inset-0 after:content-[''] focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"

function itemHref(item: ClinicalDashboardItem, productRoot: string) {
  return clinicalDashboardHref(item, productRoot)
}

function ItemBadges({ badges }: { badges?: ClinicalDashboardBadge[] }) {
  if (!badges?.length) return null
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {badges.map(badge => {
        if (badge === "new") {
          return <StatusBadge key={badge} status="new" size="sm" />
        }
        if (badge === "beta") {
          return <StatusBadge key={badge} status="beta" size="sm" />
        }
        if (badge === "leo") {
          return (
            <Badge key={badge} variant="outline" className="shrink-0 border-brand text-brand">
              Leo Powered
            </Badge>
          )
        }
        return (
          <span key={badge} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            Powered by
            <StatusBadge label="Approve" tone="success" size="sm" />
          </span>
        )
      })}
    </span>
  )
}

function DashboardRow({
  item,
  productRoot,
  iconClass,
}: {
  item: ClinicalDashboardItem
  productRoot: string
  iconClass: string
}) {
  const href = itemHref(item, productRoot)
  return (
    <li className="min-w-0 list-none">
      <div className="relative flex min-w-0 gap-3 py-3">
        <i
          className={cn(item.icon, "mt-0.5 w-5 shrink-0 text-center text-base", iconClass)}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium leading-snug text-foreground">
            <Link
              to={href}
              className={cn(STRETCHED, "inline-flex min-w-0 items-center gap-1.5 text-foreground")}
            >
              {item.label}
              {item.external ? (
                <i
                  className="fa-light fa-arrow-up-right-from-square text-xs text-muted-foreground"
                  aria-hidden
                />
              ) : null}
            </Link>
            <ItemBadges badges={item.badges} />
          </h3>
          <p className="text-sm leading-snug text-muted-foreground">{item.description}</p>
        </div>
      </div>
    </li>
  )
}

function ProgramCalendar() {
  const dateLabel = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
  }).format(new Date())

  return (
    <Tip label="Program calendar">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="relative gap-1.5 text-muted-foreground"
        aria-label={`Program calendar, ${dateLabel}, 1 item`}
      >
        <span className="text-xs font-medium tabular-nums">{dateLabel}</span>
        <i className="fa-light fa-calendar text-sm" aria-hidden />
        <BadgeCount count={1} className="absolute -top-1 -end-1" />
      </Button>
    </Tip>
  )
}

export function PrismClinicalDashboard({ productRoot }: { productRoot: string }) {
  return (
    <div className="theme-prism flex min-h-full flex-col gap-4 bg-muted pb-8">
      <PageHeader title="Clinical Education" />
      <div className="grid min-w-0 grid-cols-1 gap-4 px-4 lg:grid-cols-3 lg:gap-6 lg:px-6">
        {CLINICAL_DASHBOARD_COLUMNS.map(column => {
          const head = column.items.slice(0, (column.divideAfterIndex ?? -1) + 1)
          const tail =
            column.divideAfterIndex != null
              ? column.items.slice(column.divideAfterIndex + 1)
              : []
          const mainItems = column.divideAfterIndex != null ? head : column.items

          return (
            <Card key={column.id} className="min-w-0">
              <CardHeader className="flex flex-row items-center justify-between gap-3">
                <CardTitle asChild className="text-base font-medium">
                  <h2>{column.title}</h2>
                </CardTitle>
                {column.showCalendar ? <ProgramCalendar /> : null}
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="flex list-none flex-col p-0">
                  {mainItems.map(item => (
                    <DashboardRow
                      key={item.id}
                      item={item}
                      productRoot={productRoot}
                      iconClass={column.iconClass}
                    />
                  ))}
                </ul>
                {tail.length > 0 ? (
                  <ul className="mt-1 flex list-none flex-col border-t border-border p-0">
                    {tail.map(item => (
                      <DashboardRow
                      key={item.id}
                      item={item}
                      productRoot={productRoot}
                      iconClass={column.iconClass}
                    />
                    ))}
                  </ul>
                ) : null}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
